import CircularProgress from '@/global/components/CircularProgress'
import LinearProgress from '@/global/components/LinearProgress'
import { Link } from '@/global/components/Link'
import { transmit } from '@/lib/transmit'
import { cn } from '@/lib/utils'
import { funcionario as funcionario_api } from '@/service/api'
import { CategoriaEnum } from '@acme/auth'
import { omit, PeriodoPontoFactory } from '@acme/common'
import { Params, SN } from '@acme/core'
import { CalculoFooterFrame } from 'context/Calculo/components/CalculoFooter'
import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import SP, {
    TipoPeriodo,
    usePeriodo,
} from 'global/components/Selects/SelectPeriodo'
import ContentGuide from 'global/guides/ContentGuide'
import { useAuth } from 'hooks'
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import {
    jornada,
    JornadaDiariaQueryXor,
    JornadaMensalQueryXor,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import DigitacaoToolbar, { useFiltrosDigitacao } from '../atoms/toolbar'
import { useDigitacaoColumns } from '../columns'
import { useEdicaoEspelho } from '../edicao-espelho'
import DigitacaoErrorOverlay from '../ErrorOverlay'
import { BaterPontoButton } from '../modals/BaterPonto'
import { useDigitacaoView } from '../toggle-view'
import { montarLinhasMensal } from '../utils/linhas-mensal'
import { useDigitacaoStore } from '../utils/store'
import {
    getDigitacaoRowClassName,
    getDigitacaoRowHeight,
} from './digitacao-getters'
import { DigitacaoContext, DigitacaoContextValue } from './useDigitacaoContext'
import { useUpdateDigitacaoQueryDataNoContext } from './useDigitacaoData'

export { useDigitacaoContext } from './useDigitacaoContext'

const CalculoFooter = lazy(() =>
    import('context/Calculo/components/CalculoFooter').then(m => ({
        default: m.CalculoFooter,
    })),
)

const DataGrid = lazy(() =>
    import('@/global/guides/ContentGuide').then(m => ({
        default: m.ContentGuide.DataGrid,
    })),
)

export default function DigitacaoLayout() {
    const { hash } = useLocation()
    const hasScrolledRef = useRef(false)

    const [hasFuncionarios, setHasFuncionarios] = useState(false)
    const [cacheInvalidado, setInvalidateCache] = useState<Params>()
    const {
        decoded: { categoria },
    } = useAuth()
    const [view, setView] = useDigitacaoView()
    const [tipoFiltro, setTipoFiltro] = useFiltrosDigitacao()
    const [tipoPeriodo, setTipoPeriodo] = useState<TipoPeriodo>('competencia')
    const [[dataInicial, dataFinal], setPeriodo] = usePeriodo({
        periodoMax: 60,
    })

    const [funcionarioId, setFuncionarioId] = useStore(
        state => [state.employeeId, state.setEmployeeId] as const,
    )
    const funcionario = useStore(state => state.employee)
    const { data: funcionarioAtual } = funcionario_api.useFetchById(
        funcionarioId,
        { enabled: funcionarioId > 0 },
    )
    const centroCustoId = useStore(state => state.costCenterId)
    const localId = useStore(state => state.locationId)
    const empresa = useStore(state => state.company)

    const setCompetencia = useStore(state => state.setCompetencia)
    const competencia = useStore(state => state.competencia)
    const organizacao = useDigitacaoStore(state => state.organizacao)
    const densidade = useDigitacaoStore(state => state.densidade)

    const empresaId = empresa?.id ?? -1
    const diaInicioPonto = empresa?.diaInicioPonto
    const trocasInicioPonto = useMemo(
        () => empresa?.TrocaInicioPonto ?? [],
        [empresa?.TrocaInicioPonto],
    )
    const competenciaAtual = empresa?.competenciaAtual

    const { queryParams, enabledQueries } = useMemo<{
        queryParams: Record<string, any>
        enabledQueries: { mensal: boolean; diario: boolean }
    }>(() => {
        let key = {
            isDiarioEnabled: false,
            isMensalEnabled: false,
        } as Partial<JornadaMensalQueryXor | JornadaDiariaQueryXor> & {
            isDiarioEnabled: boolean
            isMensalEnabled: boolean
        }
        const keyMensal = {
            ...key,
            isMensalEnabled: funcionarioId > 0,
            dataInicial,
            dataFinal,
            funcionarioId: funcionarioId === -1 ? undefined : funcionarioId,
            organizar: organizacao === 'jornada' && view === 'mensal',
        }
        const keyDiario = {
            ...key,
            isDiarioEnabled: empresaId > 0,
            dia: dataInicial,
            empresaId: empresaId === -1 ? undefined : empresaId,
        }

        switch (tipoFiltro) {
            case 'centros-custo':
                key = {
                    ...(view === 'mensal' ? keyMensal : keyDiario),
                    centroCustoId:
                        centroCustoId === -1 ? undefined : centroCustoId,
                }
                break
            case 'locais':
                key = {
                    ...(view === 'mensal' ? keyMensal : keyDiario),
                    localId: localId === -1 ? undefined : localId,
                }
                key = {
                    ...key,
                    isDiarioEnabled: key.isDiarioEnabled && !!key.localId,
                    isMensalEnabled: key.isMensalEnabled && !!key.localId,
                }
                break
            default:
                key = view === 'mensal' ? keyMensal : keyDiario
                break
        }

        return {
            queryParams: omit(
                Object.fromEntries(
                    Object.entries(key).filter(([_, v]) => v !== undefined),
                ),
                ['isDiarioEnabled', 'isMensalEnabled'],
            ) as Record<string, any>,
            enabledQueries: {
                mensal: !!key.isMensalEnabled,
                diario: !!key.isDiarioEnabled,
            },
        }
    }, [
        tipoFiltro,
        view,
        dataInicial,
        dataFinal,
        funcionarioId,
        empresaId,
        centroCustoId,
        localId,
        organizacao,
    ])
    const { setQueryData } = useUpdateDigitacaoQueryDataNoContext(
        view,
        queryParams,
    )
    const { edicao, Modais: ModaisEdicaoEspelho } = useEdicaoEspelho(view)
    const columns = useDigitacaoColumns(view, edicao)

    const {
        data: mensal = [],
        isLoading: isLoadingMensal,
        error: mensalError,
        isError: isErrorMensal,
    } = jornada.mensal.useFetch({
        queryParams: queryParams as JornadaMensalQueryXor,
        enabled: enabledQueries.mensal,
        retry: (failureCount, error) => {
            if (error.statusCode !== 500) {
                return false
            }
            return failureCount < 3
        },
    })

    const {
        data: diario = [],
        isLoading: isLoadingDiario,
        error: diarioError,
        isError: isErrorDiario,
    } = jornada.diaria.useFetch({
        queryParams: queryParams as JornadaDiariaQueryXor,
        enabled: enabledQueries.diario,
        retry: (failureCount, error) => {
            if (error.statusCode !== 500) {
                return false
            }
            return failureCount < 3
        },
    })

    const isLoading = isLoadingDiario || isLoadingMensal
    const data = useMemo(() => {
        if (view !== 'mensal')
            return diario.map((it, index) => ({
                ...it,
                id: 'id' in it && it.id ? it.id : index,
            }))

        return montarLinhasMensal(
            mensal,
            SN.isTrue(funcionarioAtual?.horarioNoturno),
            [dataInicial, dataFinal],
        )
    }, [view, mensal, diario, funcionarioAtual, dataInicial, dataFinal])
    const error = useMemo(() => {
        if (isErrorDiario && view === 'diario')
            return {
                isError: true,
                message: (diarioError as any)?.message,
            }
        if (isErrorMensal && view === 'mensal')
            return {
                isError: true,
                message: (mensalError as any)?.message,
            }
        if (!isLoading && !data.length)
            return {
                isError: true,
                message: 'Nenhum registro encontrado',
            }
        return {
            isError: false,
            message: '',
        }
    }, [isErrorMensal, isErrorDiario, isLoading, data.length])

    useEffect(() => {
        if (!diaInicioPonto || !competenciaAtual) return
        const competenciaDate = dayjs(competenciaAtual, 'YYYY-MM')
        if (view === 'mensal' && tipoPeriodo === 'data') {
            const [diaInicial, diaFinal] = PeriodoPontoFactory.fromCompetencia(
                competenciaDate.format('YYYY-MM'),
                {
                    diaInicioPonto,
                    trocasInicioPonto,
                },
            ).getPeriodo(true)
            setPeriodo([diaInicial, diaFinal])
            setTipoPeriodo('competencia')
        } else if (view === 'diario' && tipoPeriodo !== 'data') {
            const hoje = dayjs(
                competenciaDate.isSame(dataInicial, 'month')
                    ? undefined
                    : dataInicial,
            ).format('YYYY-MM-DD')
            setPeriodo([hoje, hoje])
            setTipoPeriodo('data')
        }
    }, [view, competenciaAtual, diaInicioPonto, trocasInicioPonto])

    useEffect(() => {
        if (!cacheInvalidado) return
        if (typeof competencia !== 'string') return
        if (competenciaAtual !== competencia) return

        let isRegistrosPares = false
        try {
            if (view === 'mensal' && cacheInvalidado?.tipo === 'registro') {
                const j = jornada.mensal.getQueryData(queryParams as any) ?? []
                isRegistrosPares =
                    j.find(({ dia }) =>
                        dayjs(dia).isSame(cacheInvalidado?.dia, 'day'),
                    )!.registros.length %
                        2 ===
                    0
            } else if (
                view === 'diario' &&
                cacheInvalidado?.tipo === 'registro'
            ) {
                const j = jornada.diaria.getQueryData(queryParams as any) ?? []
                isRegistrosPares =
                    j.find(({ id }) => id == cacheInvalidado?.funcionarioId)!
                        .registros.length %
                        2 ===
                    0
            }
        } catch (e) {
            console.error(e)
        }

        if (
            (isRegistrosPares ||
                (cacheInvalidado && cacheInvalidado.tipo !== 'registro')) &&
            categoria != CategoriaEnum.Funcionario
        ) {
            if (view === 'mensal') {
                jornada.mensal.invalidateQueries()
            } else {
                jornada.diaria.invalidateQueries()
            }
        }
    }, [cacheInvalidado])

    useEffect(() => {
        if (empresaId === -1) return

        const subscription = transmit.subscription(
            `invalidate:empresa:${empresaId}`,
        )
        let unsubMessage: (() => void) | undefined

        subscription.create().then(() => {
            unsubMessage = subscription.onMessage<Params>(params => {
                setInvalidateCache(params)
            })
        })

        return () => {
            unsubMessage?.()
            subscription.delete()
        }
    }, [empresaId])

    useEffect(() => {
        if (funcionario && !funcionario.dtAdmissao)
            toast.warning(
                `${
                    funcionario.nome ?? 'Funcionário'
                } não possui data de admissão cadastrada`,
            )
    }, [funcionario])

    useEffect(() => {
        if (!diaInicioPonto) return
        const [competencia] = PeriodoPontoFactory.fromPeriodo(
            dataInicial,
            dataFinal,
        ).getCompetencias({
            diaInicioPonto,
            trocasInicioPonto,
        })
        if (competencia) setCompetencia(competencia)
    }, [dataInicial, dataFinal, diaInicioPonto, trocasInicioPonto])

    useEffect(() => {
        if (!hash || hasScrolledRef.current || isLoading || !data.length) return
        if (view !== 'mensal') return
        if (!/^#\d{4}-\d{2}-\d{2}$/.test(hash)) return

        const dia = hash.replace('#', '')
        const rowIndex = data.findIndex((row: any) => row.dia === dia)
        if (rowIndex === -1) return

        requestAnimationFrame(() => {
            const row = document.querySelector(`.row-dia-${dia}`)
            if (row) {
                row.scrollIntoView({ behavior: 'smooth', block: 'center' })
                hasScrolledRef.current = true
                history.replaceState(
                    '',
                    document.title,
                    window.location.pathname + window.location.search,
                )
                row.classList.add('animate-highlight')
                setTimeout(() => {
                    row.classList.remove('animate-highlight')
                }, 3000)
            }
        })
    }, [hash, isLoading, data, view])

    const getRowClassNameWithDia = useMemo(() => {
        return (params: { row: any }) => {
            const baseClass = getDigitacaoRowClassName(params)
            const diaClass = params.row.dia ? `row-dia-${params.row.dia}` : ''
            return [baseClass, diaClass].filter(Boolean).join(' ')
        }
    }, [])

    // Referências estáveis: slots/components inline remontavam o Toolbar a cada
    // render, o que remontava o observer da query de funcionário (staleTime 0),
    // disparando refetch → re-render → loop infinito.
    const gridComponents = useMemo(
        () => ({
            ErrorOverlay: () => (
                <DigitacaoErrorOverlay message={error.message} />
            ),
        }),
        [error.message],
    )
    const gridSlots = useMemo(
        () => ({
            Toolbar: () => (
                <DigitacaoToolbar
                    view={view}
                    periodo={[dataInicial, dataFinal]}
                />
            ),
        }),
        [view, dataInicial, dataFinal],
    )

    const contextValue = useMemo<DigitacaoContextValue>(
        () => ({
            data,
            view,
            changeView: setView,
            tipoFiltro,
            changeTipoFiltro: setTipoFiltro,
            periodo: [dataInicial, dataFinal],
            queryParams,
            setQueryData,
            existemFuncionarios: hasFuncionarios,
        }),
        // setQueryData omitido de propósito: sua identidade muda a cada render,
        // mas seu comportamento depende só de view + queryParams (já nas deps).
        [
            data,
            view,
            setView,
            tipoFiltro,
            setTipoFiltro,
            dataInicial,
            dataFinal,
            queryParams,
            hasFuncionarios,
        ],
    )

    if (!empresa) {
        return (
            <ContentGuide>
                <ContentGuide.Card>
                    <div className="flex flex-col justify-center items-center gap-4">
                        <CircularProgress size={140} />
                        <span className="text-primary text-3xl">
                            Carregando empresa...
                        </span>
                        <span className="text-primary text-sm max-w-50 text-center">
                            Se isso demorar mais que alguns segundos, tente{' '}
                            <Link
                                className="cursor-pointer"
                                onClick={() => window.location.reload()}
                            >
                                recarregar a página.
                            </Link>
                        </span>
                    </div>
                </ContentGuide.Card>
            </ContentGuide>
        )
    }

    return (
        <DigitacaoContext.Provider value={contextValue}>
            <ContentGuide>
                <ContentGuide.Card>
                    <ContentGuide.Header
                        label={[
                            'Manutenção do ponto',
                            'Digitação',
                            view === 'mensal' ? 'Mensal' : 'Diário',
                        ]}
                    >
                        <ContentGuide.Filtros
                            SelectEmpresas
                            SelectCentroCusto={
                                categoria !== CategoriaEnum.Funcionario &&
                                tipoFiltro === 'centros-custo'
                            }
                            SelectLocal={
                                categoria !== CategoriaEnum.Funcionario &&
                                tipoFiltro === 'locais'
                            }
                            SelectFuncionario={
                                categoria !== CategoriaEnum.Funcionario &&
                                view === 'mensal'
                                    ? {
                                          button: true,
                                          centroCustoId:
                                              tipoFiltro === 'centros-custo'
                                                  ? centroCustoId
                                                  : undefined,
                                          localId:
                                              tipoFiltro === 'locais'
                                                  ? localId
                                                  : undefined,
                                          onHasFuncionariosChange:
                                              setHasFuncionarios,
                                      }
                                    : false
                            }
                        >
                            {categoria === CategoriaEnum.Funcionario && (
                                <BaterPontoButton />
                            )}
                            <SP.Root
                                onChange={setPeriodo}
                                value={[dataInicial, dataFinal]}
                                allowedTypes={
                                    view === 'mensal'
                                        ? ['competencia', 'periodo']
                                        : ['data']
                                }
                                type={tipoPeriodo}
                                setType={setTipoPeriodo}
                                width={320}
                            >
                                <SP.DateSelector />
                                <SP.Buttons />
                            </SP.Root>
                        </ContentGuide.Filtros>
                    </ContentGuide.Header>
                    <div className="flex flex-col overflow-hidden flex-1 w-full">
                        <div className="overflow-hidden w-full flex-1">
                            <LinearProgress
                                key="loading"
                                className={cn(
                                    'h-1 w-full opacity-0 rounded-sm',
                                    {
                                        'opacity-100': isLoading,
                                    },
                                    'transition-opacity duration-300 ease-in-out',
                                )}
                            />
                            <Suspense
                                fallback={
                                    <div
                                        className={cn(
                                            'size-full overflow-hidden flex flex-col',
                                            'content-between flex-1 border rounded-t-md',
                                        )}
                                    >
                                        <div className="h-14 flex border-b" />
                                        <div className="flex-1 flex justify-center items-center">
                                            <CircularProgress size={48} />
                                        </div>
                                    </div>
                                }
                            >
                                <DataGrid
                                    key={columns.colsHash}
                                    loading={!data?.length && isLoading}
                                    rows={data}
                                    getRowHeight={getDigitacaoRowHeight}
                                    columns={columns.columns}
                                    hideFooter
                                    components={gridComponents}
                                    slots={gridSlots}
                                    onRowClick={value => {
                                        if (
                                            view === 'diario' &&
                                            'funcionarioId' in value.row
                                        )
                                            setFuncionarioId(
                                                value.row.funcionarioId,
                                            )
                                    }}
                                    getRowClassName={getRowClassNameWithDia}
                                    error={error.isError}
                                    pageSize={-1}
                                    density={densidade}
                                />
                            </Suspense>
                        </div>
                        {view === 'mensal' ? (
                            <Suspense
                                fallback={
                                    <CalculoFooterFrame>
                                        <CalculoFooterFrame.LoadingContent />
                                    </CalculoFooterFrame>
                                }
                            >
                                <CalculoFooter
                                    key={`${funcionarioId}  ${!!funcionario}`}
                                    frameOnly={
                                        !hasFuncionarios &&
                                        categoria !== CategoriaEnum.Funcionario
                                    }
                                />
                            </Suspense>
                        ) : (
                            <CalculoFooterFrame>
                                Total de linhas: {data.length}
                            </CalculoFooterFrame>
                        )}
                    </div>
                </ContentGuide.Card>
                <Outlet key="outlet" />
            </ContentGuide>
            {ModaisEdicaoEspelho}
        </DigitacaoContext.Provider>
    )
}
